import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft } from '@tabler/icons-react';

export interface MobileBackBarProps {
  /**
   * Where to go when there is no in-app history to pop — typically the current
   * module's landing page. Defaults to the launcher.
   */
  fallbackHref?: string;
}

/**
 * Slim back row shown below the header on mobile.
 *
 * The hamburger is a jump menu: using it to go back means re-finding where you
 * were, and it cannot return you to a detail page's parent at all. This gives
 * that a dedicated control.
 *
 * It sits on the page background rather than inside the forest header — in the
 * band it crowded the hamburger and logo and read as a third piece of chrome
 * competing for the same corner.
 *
 * Deliberately outside the scrolling area so it stays reachable on long pages
 * without scrolling back to the top.
 */
export function MobileBackBar({ fallbackHref = '/' }: MobileBackBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // React Router sets key to 'default' when the app booted directly on this URL
  // — a deep link, refresh, or shared URL — meaning there is no in-app history.
  // Popping then would walk the user out of the app entirely, so fall back to
  // the module landing page instead.
  const hasInAppHistory = location.key !== 'default';

  const handleBack = () => {
    if (hasInAppHistory) navigate(-1);
    else navigate(fallbackHref);
  };

  return (
    <div className="md:hidden flex-shrink-0 px-4 pt-3 pb-1 bg-[var(--background-page-body)]">
      <button
        type="button"
        onClick={handleBack}
        className="inline-flex items-center gap-1.5 -ml-1 px-1 py-1 rounded-md text-body-s font-medium text-[var(--text-content-sub-title)] active:text-[var(--color-forest)] focus:outline-none"
        aria-label={t('common.back')}
      >
        <IconArrowLeft size={18} />
        {t('common.back')}
      </button>
    </div>
  );
}
